import React from 'react';

import {
  Box,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Collapse,
} from '@material-ui/core';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import * as yup from 'yup';

import ColoredButton from 'components/ColoredButton';
import CustomAccordion from 'components/CustomAccordion';
import ErrorBoundary from 'components/ErrorBoundary';
import FormSelect from 'components/FormSelect/FormSelect';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import dsarApi from 'config/api/dsar/dsar';
import type { DsarCase, DsarCaseUpdatePayload, DsarDomainValueSummary, DsarEligibleAsset } from 'config/api/dsar/dsar.types';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import validation_messages from 'messages/validation_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';

import useStyles from './DsarCaseDetailPage.styles';

const REQUEST_TYPE_GROUP = 'request_type';
const CASE_STATUS_GROUP = 'case_status';
const IDENTITY_STATUS_GROUP = 'identity_verification_status';
const FINAL_REQUEST_TYPE_STATUS_CODES = ['completed', 'partially_completed', 'not_applicable', 'deferred', 'not_possible'];
const BLOCKING_REQUEST_TYPE_STATUS_CODES = ['deferred', 'not_possible'];
const PORTABILITY_REQUEST_TYPE_CODES = ['access', 'portability'];
const PENDING_DETAILS_LIMIT = 10;

type DsarCaseDetailLocationState = {
  openEdit?: boolean;
};

type FieldProps = {
  label: string;
  value?: React.ReactNode;
};

type TabPanelProps = {
  children: React.ReactNode;
  index: number;
  value: number;
};

type FormValues = {
  requesterName: string;
  requesterEmail: string;
  requesterPhone: string;
  requesterCustomerNumber: string;
  requesterSsn: string;
  requesterAddressLine1: string;
  requesterAddressLine2: string;
  requesterPostalCode: string;
  requesterCity: string;
  requesterCountry: string;
  identityVerificationStatusId: number | '';
  identityVerificationNotes: string;
  statusId: number | '';
  receivedAt: string;
  dueDate: string;
  requestTypeIds: number[];
  adminInstructionComment: string;
  instructionLink: string;
  portabilityInstruction: string;
  portabilityOutputLink: string;
  responseSummary: string;
};

type CommunicationLogFormValues = {
  communicationType: string;
  comment: string;
};

const EMPTY_FORM_VALUES: FormValues = {
  requesterName: '',
  requesterEmail: '',
  requesterPhone: '',
  requesterCustomerNumber: '',
  requesterSsn: '',
  requesterAddressLine1: '',
  requesterAddressLine2: '',
  requesterPostalCode: '',
  requesterCity: '',
  requesterCountry: '',
  identityVerificationStatusId: '',
  identityVerificationNotes: '',
  statusId: '',
  receivedAt: '',
  dueDate: '',
  requestTypeIds: [],
  adminInstructionComment: '',
  instructionLink: '',
  portabilityInstruction: '',
  portabilityOutputLink: '',
  responseSummary: '',
};

const toOptions = (values?: DsarDomainValueSummary[]) => (values || []).map(value => ({ key: value.id, label: value.name }));

const cleanOptional = (value: string) => value.trim() || undefined;

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  return value.split('T')[0];
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  return value.split('T')[0];
};

const formatOptional = (value?: string | null) => value || '-';

const buildInitialValues = (
  dsarCase: DsarCase,
  caseStatusValues: DsarDomainValueSummary[],
  identityStatusValues: DsarDomainValueSummary[],
): FormValues => ({
  requesterName: dsarCase.requesterName || '',
  requesterEmail: dsarCase.requesterEmail || '',
  requesterPhone: dsarCase.requesterPhone || '',
  requesterCustomerNumber: dsarCase.requesterCustomerNumber || '',
  requesterSsn: dsarCase.requesterSsn || '',
  requesterAddressLine1: dsarCase.requesterAddressLine1 || '',
  requesterAddressLine2: dsarCase.requesterAddressLine2 || '',
  requesterPostalCode: dsarCase.requesterPostalCode || '',
  requesterCity: dsarCase.requesterCity || '',
  requesterCountry: dsarCase.requesterCountry || '',
  identityVerificationStatusId: dsarCase.identityVerificationStatus?.id || identityStatusValues[0]?.id || '',
  identityVerificationNotes: dsarCase.identityVerificationNotes || '',
  statusId: dsarCase.status?.id || caseStatusValues[0]?.id || '',
  receivedAt: toDateInputValue(dsarCase.receivedAt),
  dueDate: toDateInputValue(dsarCase.dueDate),
  requestTypeIds: (dsarCase.requestTypes || []).map(({ requestType }) => requestType.id),
  adminInstructionComment: dsarCase.adminInstructionComment || '',
  instructionLink: dsarCase.instructionLink || '',
  portabilityInstruction: dsarCase.portabilityInstruction || '',
  portabilityOutputLink: dsarCase.portabilityOutputLink || '',
  responseSummary: dsarCase.responseSummary || '',
});

const Field: React.FC<FieldProps> = ({ label, value }) => {
  const styles = useStyles();

  return (
    <div className={styles.detailField}>
      <Typography className={styles.detailLabel} variant='body2'>
        {label}
      </Typography>
      <Typography className={styles.detailValue} variant='body1'>
        {value || '-'}
      </Typography>
    </div>
  );
};

const TabPanel: React.FC<TabPanelProps> = ({ children, index, value }) => (
  <div aria-labelledby={`dsar-case-tab-${index}`} hidden={value !== index} id={`dsar-case-tabpanel-${index}`} role='tabpanel'>
    {value === index && <Box>{children}</Box>}
  </div>
);

const getApiErrorMessage = (error: any) => {
  const detail = error?.response?.data;
  if (!detail || typeof detail !== 'object') return null;

  const [firstValue] = Object.values(detail);
  if (Array.isArray(firstValue)) return firstValue[0];
  if (typeof firstValue === 'string') return firstValue;
  return null;
};

const getRetentionNotice = (dsarCase: DsarCase, t: (key: any, options?: Record<string, string>) => string) => {
  if (dsarCase.identifiersAnonymizedAt) {
    return t(dsar_messages.cases.detail.retention_anonymized_notice, {
      date: formatDate(dsarCase.identifiersAnonymizedAt),
    });
  }
  if (dsarCase.retentionUntil) {
    return t(dsar_messages.cases.detail.retention_anonymization_notice, {
      date: formatDate(dsarCase.retentionUntil),
    });
  }
  return t(dsar_messages.cases.detail.retention_pending_notice);
};

type RequestTypeAssetDetail = {
  assetId: number;
  assetName: string;
  ownerName: string;
  statusName: string;
  blockingReason?: string | null;
  plannedDate?: string | null;
};

const DsarCaseDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<DsarCaseDetailLocationState>();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const { id } = useParams<{ id: string }>();
  const [editMode, setEditMode] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const [assetSelectionDialogOpen, setAssetSelectionDialogOpen] = React.useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = React.useState<number[]>([]);
  const [expandedSummaryCodes, setExpandedSummaryCodes] = React.useState<string[]>([]);

  const { data: caseResponse, isLoading, error } = useQuery(['DSAR_CASE_DETAIL', id], () => dsarApi.getSingleCase(Number(id)));
  const { data: requestTypeValuesResponse, isLoading: requestTypeValuesLoading } = useQuery(
    ['DSAR_DOMAIN_VALUES', REQUEST_TYPE_GROUP],
    () => dsarApi.getDomainValues({ group: REQUEST_TYPE_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: caseStatusValuesResponse, isLoading: caseStatusValuesLoading } = useQuery(
    ['DSAR_DOMAIN_VALUES', CASE_STATUS_GROUP],
    () => dsarApi.getDomainValues({ group: CASE_STATUS_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: identityStatusValuesResponse, isLoading: identityStatusValuesLoading } = useQuery(
    ['DSAR_DOMAIN_VALUES', IDENTITY_STATUS_GROUP],
    () => dsarApi.getDomainValues({ group: IDENTITY_STATUS_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: caseAssetsResponse, isLoading: caseAssetsLoading } = useQuery(
    ['DSAR_CASE_ASSETS_FOR_CASE', id],
    () =>
      dsarApi.getAllCaseAssets({
        dsar_case_id: Number(id),
        limit: 200,
      }),
    {
      enabled: Boolean(id),
      retry: false,
      staleTime: 30000,
    },
  );
  const { data: eligibleAssetsResponse, isLoading: eligibleAssetsLoading } = useQuery(
    ['DSAR_CASE_ELIGIBLE_ASSETS', id],
    () => dsarApi.getEligibleAssets(Number(id)),
    {
      enabled: assetSelectionDialogOpen && Boolean(id),
      retry: false,
      staleTime: 30000,
    },
  );

  const distributeMutation = useMutation((assetIds: number[]) => dsarApi.distributeCaseToAssets(Number(id), { asset_ids: assetIds }), {
    onSuccess: response => {
      enqueueSnackbar(
        t(dsar_messages.cases.detail.distribution_result, {
          assetsCreated: response.data.assetsCreated,
          requestTypesCreated: response.data.requestTypeResultsCreated,
        }),
        { variant: 'success' },
      );
      queryClient.invalidateQueries('DSAR_CASES_GET_ALL');
      queryClient.invalidateQueries(['DSAR_CASE_DETAIL', id]);
      queryClient.invalidateQueries(['DSAR_CASE_ASSETS_FOR_CASE', id]);
      setAssetSelectionDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
    },
  });

  const deleteMutation = useMutation(() => dsarApi.deleteCase(Number(id)), {
    onSuccess: () => {
      enqueueSnackbar(t(general_messages.data_removed), { variant: 'success' });
      queryClient.invalidateQueries('DSAR_CASES_GET_ALL');
      history.push(PATHS.DSAR_CASES);
    },
    onError: () => {
      enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
    },
  });

  const dsarCase = caseResponse?.data;
  const caseAssets = caseAssetsResponse?.data.results || [];
  const eligibleAssets = React.useMemo<DsarEligibleAsset[]>(() => eligibleAssetsResponse?.data || [], [eligibleAssetsResponse]);
  const domainValuesLoading = requestTypeValuesLoading || caseStatusValuesLoading || identityStatusValuesLoading;
  const requestTypeValues: DsarDomainValueSummary[] = requestTypeValuesResponse?.data || [];
  const caseStatusValues: DsarDomainValueSummary[] = caseStatusValuesResponse?.data || [];
  const identityStatusValues: DsarDomainValueSummary[] = identityStatusValuesResponse?.data || [];
  const verifiedIdentityStatusIds = React.useMemo(
    () => identityStatusValues.filter(value => value.code === 'verified').map(value => value.id),
    [identityStatusValues],
  );
  const closedStatusValue = React.useMemo(() => caseStatusValues.find(value => value.code === 'closed'), [caseStatusValues]);
  const canDistribute = dsarCase?.identityVerificationStatus.code === 'verified' && Boolean(dsarCase.identityVerificationNotes?.trim());
  const canCloseCase = Boolean(closedStatusValue) && Boolean(dsarCase?.communicationLogs?.length);
  const requestTypesLocked = Boolean(dsarCase?.distributedAt);
  const identityLocked = Boolean(dsarCase?.distributedAt);
  const retentionNotice = dsarCase ? getRetentionNotice(dsarCase, t) : '';
  const communicationTypeOptions = React.useMemo(
    () => [
      { key: 'response', label: t(dsar_messages.cases.detail.communication_type_response) },
      { key: 'extension_notice', label: t(dsar_messages.cases.detail.communication_type_extension_notice) },
      {
        key: 'identity_verification_request',
        label: t(dsar_messages.cases.detail.communication_type_identity_verification_request),
      },
      {
        key: 'identity_verification_approved',
        label: t(dsar_messages.cases.detail.communication_type_identity_verification_approved),
      },
      {
        key: 'additional_information_request',
        label: t(dsar_messages.cases.detail.communication_type_additional_information_request),
      },
      { key: 'partial_response', label: t(dsar_messages.cases.detail.communication_type_partial_response) },
      { key: 'final_response', label: t(dsar_messages.cases.detail.communication_type_final_response) },
      { key: 'other', label: t(dsar_messages.cases.detail.communication_type_other) },
    ],
    [t],
  );

  const validationSchema = React.useMemo(
    () =>
      yup.object({
        requesterName: yup.string().required(t(validation_messages.required)),
        requesterEmail: yup.string().email(t(validation_messages.email_valid)),
        identityVerificationStatusId: yup.number().required(t(validation_messages.required)),
        identityVerificationNotes: yup
          .string()
          .test('identity-verification-notes-required-when-verified', t(validation_messages.required), (value, context) => {
            const { identityVerificationStatusId } = context.parent as FormValues;
            if (!verifiedIdentityStatusIds.includes(Number(identityVerificationStatusId))) return true;
            return Boolean(value?.trim());
          }),
        statusId: yup.number().required(t(validation_messages.required)),
        receivedAt: yup.string().required(t(validation_messages.required)),
        dueDate: yup
          .string()
          .required(t(validation_messages.required))
          .test('due-date-after-received-at', t(validation_messages.date_to_cannot_be_before_date_from), (value, context) => {
            const { receivedAt } = context.parent as FormValues;
            if (!value || !receivedAt) return true;
            return new Date(value) >= new Date(receivedAt);
          }),
        requestTypeIds: yup.array().of(yup.number()).min(1, t(validation_messages.required)),
      }),
    [t, verifiedIdentityStatusIds],
  );

  const formik = useFormik<FormValues>({
    initialValues: dsarCase ? buildInitialValues(dsarCase, caseStatusValues, identityStatusValues) : EMPTY_FORM_VALUES,
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });
  const communicationLogFormik = useFormik<CommunicationLogFormValues>({
    initialValues: {
      communicationType: 'response',
      comment: '',
    },
    validationSchema: yup.object({
      communicationType: yup.string().required(t(validation_messages.required)),
      comment: yup.string().required(t(validation_messages.required)),
    }),
    onSubmit: () => undefined,
  });
  const showPortabilityFields = requestTypeValues.some(
    requestType => formik.values.requestTypeIds.includes(requestType.id) && PORTABILITY_REQUEST_TYPE_CODES.includes(requestType.code),
  );
  const showReadOnlyPortabilityFields = Boolean(
    dsarCase?.requestTypes.some(({ requestType }) => PORTABILITY_REQUEST_TYPE_CODES.includes(requestType.code)),
  );

  const caseMutation = useMutation((payload: DsarCaseUpdatePayload) => dsarApi.updateCase(Number(id), payload), {
    onSuccess: response => {
      enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
      queryClient.setQueryData(['DSAR_CASE_DETAIL', id], { data: response.data });
      queryClient.invalidateQueries('DSAR_CASES_GET_ALL');
    },
    onError: mutationError => {
      enqueueSnackbar(getApiErrorMessage(mutationError) || t(general_messages.api_error), { variant: 'error' });
    },
  });

  const buildPayload = React.useCallback(
    (values: FormValues, statusIdOverride?: number) => ({
      requester_name: values.requesterName.trim(),
      requester_email: cleanOptional(values.requesterEmail),
      requester_phone: cleanOptional(values.requesterPhone),
      requester_customernumber: cleanOptional(values.requesterCustomerNumber),
      requester_ssn: cleanOptional(values.requesterSsn),
      requester_address_line1: cleanOptional(values.requesterAddressLine1),
      requester_address_line2: cleanOptional(values.requesterAddressLine2),
      requester_postalcode: cleanOptional(values.requesterPostalCode),
      requester_city: cleanOptional(values.requesterCity),
      requester_country: cleanOptional(values.requesterCountry),
      identity_verification_status_id: Number(values.identityVerificationStatusId),
      identity_verification_notes: cleanOptional(values.identityVerificationNotes),
      status_id: statusIdOverride || Number(values.statusId),
      received_at: values.receivedAt,
      due_date: values.dueDate,
      admin_instruction_comment: cleanOptional(values.adminInstructionComment),
      instruction_link: cleanOptional(values.instructionLink),
      portability_instruction: cleanOptional(values.portabilityInstruction),
      portability_output_link: cleanOptional(values.portabilityOutputLink),
      response_summary: cleanOptional(values.responseSummary),
      request_type_ids: values.requestTypeIds,
    }),
    [],
  );

  const handleBack = React.useCallback(() => {
    history.push(PATHS.DSAR_CASES);
  }, [history]);

  const handleOpenAssetCases = React.useCallback(() => {
    history.push(PATHS.DSAR_CASE_ASSETS);
  }, [history]);

  const handleOpenEdit = React.useCallback(() => {
    setEditMode(true);
  }, []);

  const handleCancelEdit = React.useCallback(() => {
    if (dsarCase) {
      formik.resetForm({ values: buildInitialValues(dsarCase, caseStatusValues, identityStatusValues) });
    }
    setEditMode(false);
  }, [caseStatusValues, dsarCase, formik, identityStatusValues]);

  const handleDistribute = React.useCallback(() => {
    setAssetSelectionDialogOpen(true);
  }, []);

  const handleCloseAssetSelectionDialog = React.useCallback(() => {
    if (distributeMutation.isLoading) return;
    setAssetSelectionDialogOpen(false);
  }, [distributeMutation.isLoading]);

  const handleToggleEligibleAsset = React.useCallback((assetId: number) => {
    setSelectedAssetIds(current =>
      current.includes(assetId) ? current.filter(selectedAssetId => selectedAssetId !== assetId) : [...current, assetId],
    );
  }, []);

  const handleSelectAllEligibleAssets = React.useCallback(() => {
    setSelectedAssetIds(eligibleAssets.map(asset => asset.id));
  }, [eligibleAssets]);

  const handleClearEligibleAssets = React.useCallback(() => {
    setSelectedAssetIds([]);
  }, []);

  const handleSendSelectedAssets = React.useCallback(() => {
    if (!selectedAssetIds.length) return;
    distributeMutation.mutate(selectedAssetIds);
  }, [distributeMutation, selectedAssetIds]);

  const handleDelete = React.useCallback(async () => {
    if (!dsarCase) return;

    const confirmed = await showConfirmationModal({
      title: t(dsar_messages.cases.delete_confirmation.title),
      body: t(dsar_messages.cases.delete_confirmation.body, {
        caseReference: dsarCase.caseReference,
      }),
      yesLabel: t(general_messages.delete),
      noLabel: t(general_messages.cancel),
    });

    if (confirmed) {
      deleteMutation.mutate();
    }
  }, [deleteMutation, dsarCase, showConfirmationModal, t]);

  const saveCaseChanges = React.useCallback(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) {
      formik.setTouched(
        {
          requesterName: true,
          requesterEmail: true,
          identityVerificationStatusId: true,
          identityVerificationNotes: true,
          statusId: true,
          receivedAt: true,
          dueDate: true,
          requestTypeIds: true,
          instructionLink: true,
          portabilityOutputLink: true,
        },
        true,
      );
      enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
      return;
    }

    caseMutation.mutate(buildPayload(formik.values));
  }, [buildPayload, caseMutation, enqueueSnackbar, formik, t]);

  const communicationLogMutation = useMutation(
    (payload: { communication_type: string; comment: string }) => dsarApi.addCommunicationLog(Number(id), payload),
    {
      onSuccess: () => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        communicationLogFormik.resetForm();
        queryClient.invalidateQueries(['DSAR_CASE_DETAIL', id]);
      },
      onError: mutationError => {
        enqueueSnackbar(getApiErrorMessage(mutationError) || t(general_messages.api_error), { variant: 'error' });
      },
    },
  );

  const handleAddCommunicationLog = React.useCallback(async () => {
    const errors = await communicationLogFormik.validateForm();
    if (Object.keys(errors).length) {
      communicationLogFormik.setTouched(
        {
          communicationType: true,
          comment: true,
        },
        true,
      );
      return;
    }

    communicationLogMutation.mutate({
      communication_type: communicationLogFormik.values.communicationType,
      comment: communicationLogFormik.values.comment,
    });
  }, [communicationLogFormik, communicationLogMutation]);

  const handleCloseCase = React.useCallback(async () => {
    if (!dsarCase || !closedStatusValue || !dsarCase.communicationLogs?.length) return;

    const closingValues = {
      ...formik.values,
      statusId: closedStatusValue.id,
    };
    const errors = await formik.validateForm(closingValues);
    if (Object.keys(errors).length) {
      formik.setValues(closingValues, false);
      formik.setTouched(
        {
          requesterName: true,
          requesterEmail: true,
          identityVerificationStatusId: true,
          identityVerificationNotes: true,
          statusId: true,
          receivedAt: true,
          dueDate: true,
          requestTypeIds: true,
          instructionLink: true,
          portabilityOutputLink: true,
        },
        true,
      );
      return;
    }

    const confirmed = await showConfirmationModal({
      title: t(dsar_messages.cases.detail.close_case_confirmation_title),
      body: t(dsar_messages.cases.detail.close_case_confirmation_body, {
        caseReference: dsarCase.caseReference,
      }),
      yesLabel: t(dsar_messages.cases.detail.tab_close_case),
      noLabel: t(general_messages.cancel),
    });

    if (!confirmed) return;

    caseMutation.mutate(buildPayload(closingValues, closedStatusValue.id), {
      onSuccess: response => {
        enqueueSnackbar(t(dsar_messages.cases.detail.close_case_success), { variant: 'success' });
        queryClient.setQueryData(['DSAR_CASE_DETAIL', id], { data: response.data });
        queryClient.invalidateQueries('DSAR_CASES_GET_ALL');
        setEditMode(false);
        setActiveTab(2);
      },
    });
  }, [buildPayload, caseMutation, closedStatusValue, dsarCase, enqueueSnackbar, formik, id, queryClient, showConfirmationModal, t]);

  const handleRequestTypeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const requestTypeId = Number(event.target.name);
      const nextValues = event.target.checked
        ? [...formik.values.requestTypeIds, requestTypeId]
        : formik.values.requestTypeIds.filter(selectedId => selectedId !== requestTypeId);
      formik.setFieldTouched('requestTypeIds', true, false);
      formik.setFieldValue('requestTypeIds', nextValues);
    },
    [formik],
  );

  React.useEffect(() => {
    if (!location.state?.openEdit) return;
    setEditMode(true);
    setActiveTab(0);
    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: {},
    });
  }, [history, location]);

  React.useEffect(() => {
    if (!assetSelectionDialogOpen) return;
    setSelectedAssetIds(eligibleAssets.map(asset => asset.id));
  }, [assetSelectionDialogOpen, eligibleAssets]);

  const requestTypeSummaries = React.useMemo(
    () =>
      dsarCase?.requestTypes.map(({ requestType }) => {
        const matchingResults = caseAssets.flatMap(caseAsset =>
          caseAsset.requestTypeResults.filter(result => result.requestType.code === requestType.code),
        );
        const completedAssets = matchingResults.filter(result =>
          ['completed', 'partially_completed', 'not_applicable'].includes(result.handlingStatus.code),
        ).length;
        const notCompletedAssets = matchingResults.filter(
          result => !FINAL_REQUEST_TYPE_STATUS_CODES.includes(result.handlingStatus.code),
        ).length;
        const blockingAssets = caseAssets.filter(caseAsset =>
          caseAsset.requestTypeResults.some(
            result =>
              result.requestType.code === requestType.code && BLOCKING_REQUEST_TYPE_STATUS_CODES.includes(result.handlingStatus.code),
          ),
        );
        const assetDetails = caseAssets.reduce<{
          blocking: RequestTypeAssetDetail[];
          pending: RequestTypeAssetDetail[];
        }>(
          (details, caseAsset) => {
            const matchingResult = caseAsset.requestTypeResults.find(result => result.requestType.code === requestType.code);
            if (!matchingResult) return details;

            const detail = {
              assetId: caseAsset.asset.id,
              assetName: caseAsset.asset.name,
              ownerName: caseAsset.asset.owner.fullName,
              statusName: matchingResult.handlingStatus.name,
              blockingReason: matchingResult.blockingReason,
              plannedDate: matchingResult.earliestPossibleActionDate,
            };

            if (BLOCKING_REQUEST_TYPE_STATUS_CODES.includes(matchingResult.handlingStatus.code)) {
              details.blocking.push(detail);
              return details;
            }
            if (!FINAL_REQUEST_TYPE_STATUS_CODES.includes(matchingResult.handlingStatus.code)) {
              details.pending.push(detail);
            }
            return details;
          },
          { blocking: [], pending: [] },
        );
        const plannedDates = matchingResults
          .map(result => result.earliestPossibleActionDate)
          .filter((value): value is string => Boolean(value))
          .sort();

        return {
          code: requestType.code,
          name: requestType.name,
          completedAssets,
          notCompletedAssets,
          blockingAssetNames: blockingAssets.map(caseAsset => caseAsset.asset.name),
          blockingDetails: assetDetails.blocking.sort((left, right) =>
            String(left.plannedDate || '').localeCompare(String(right.plannedDate || '')),
          ),
          pendingDetails: assetDetails.pending.sort((left, right) =>
            String(left.plannedDate || '').localeCompare(String(right.plannedDate || '')),
          ),
          latestPlannedDate: plannedDates[plannedDates.length - 1] || null,
        };
      }) || [],
    [caseAssets, dsarCase],
  );
  React.useEffect(() => {
    setExpandedSummaryCodes(currentCodes => {
      const blockingCodes = requestTypeSummaries.filter(summary => summary.blockingDetails.length).map(summary => summary.code);
      const nextCodes = Array.from(new Set([...currentCodes, ...blockingCodes]));
      return nextCodes.length === currentCodes.length && nextCodes.every(code => currentCodes.includes(code)) ? currentCodes : nextCodes;
    });
  }, [requestTypeSummaries]);

  const toggleSummaryDetails = React.useCallback((code: string) => {
    setExpandedSummaryCodes(currentCodes =>
      currentCodes.includes(code) ? currentCodes.filter(currentCode => currentCode !== code) : [...currentCodes, code],
    );
  }, []);
  const selectedAssetsMessage = selectedAssetIds.length
    ? t(dsar_messages.cases.detail.send_assets_selected_count, { count: selectedAssetIds.length })
    : t(dsar_messages.cases.detail.send_assets_required);
  let assetSelectionContent: React.ReactNode = (
    <Typography className={styles.helperText} variant='body2'>
      {t(dsar_messages.cases.detail.send_assets_empty)}
    </Typography>
  );
  if (eligibleAssetsLoading) {
    assetSelectionContent = <Loader inner />;
  } else if (eligibleAssets.length) {
    assetSelectionContent = (
      <TableContainer className={styles.assetSelectionTableContainer}>
        <Table aria-label={t(dsar_messages.cases.detail.send_assets_dialog_title)} size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' />
              <TableCell>{t(dsar_messages.cases.detail.send_assets_asset_name)}</TableCell>
              <TableCell>{t(dsar_messages.cases.detail.send_assets_owner)}</TableCell>
              <TableCell>{t(dsar_messages.cases.detail.send_assets_asset_type)}</TableCell>
              <TableCell>{t(dsar_messages.cases.detail.send_assets_personal_data)}</TableCell>
              <TableCell>{t(dsar_messages.cases.detail.send_assets_sensitive_personal_data)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eligibleAssets.map(asset => {
              const checked = selectedAssetIds.includes(asset.id);
              return (
                <TableRow key={asset.id} className={checked ? styles.assetSelectionRowSelected : undefined} hover selected={checked}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={checked}
                      color='primary'
                      inputProps={{ 'aria-label': asset.name }}
                      onChange={() => handleToggleEligibleAsset(asset.id)}
                    />
                  </TableCell>
                  <TableCell>{asset.name}</TableCell>
                  <TableCell>
                    <Typography variant='body2'>{asset.owner.fullName}</Typography>
                    <Typography color='textSecondary' variant='caption'>
                      {asset.owner.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatOptional(asset.assetType)}</TableCell>
                  <TableCell>{formatOptional(asset.identifiableInformation)}</TableCell>
                  <TableCell>{formatOptional(asset.sensitiveIndividualInformation)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (isLoading) return <Loader inner />;

  if (error || !dsarCase) {
    return (
      <ErrorBoundary>
        <Container>
          <Typography color='error'>{t(general_messages.something_went_wrong)}</Typography>
        </Container>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader backButton onBack={handleBack}>
          {`${t(dsar_messages.cases.detail.title)} ${dsarCase.caseReference}`}
        </ManagePageHeader>
        <div className={styles.pageWrapper}>
          <Paper className={styles.heroCard} elevation={0}>
            <div className={styles.heroTop}>
              <div>
                <Typography variant='h6'>{dsarCase.caseReference}</Typography>
                <Typography color='textSecondary'>
                  {`${dsarCase.requesterName} - ${dsarCase.status.name} - ${t(dsar_messages.cases.create_dialog.due_date)}: ${formatDate(
                    dsarCase.dueDate,
                  )}`}
                </Typography>
              </div>
              <div className={styles.heroActions}>
                <ColoredButton
                  className={styles.heroActionButton}
                  customColor='secondary'
                  disabled={false}
                  icon={false}
                  onClick={handleOpenAssetCases}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(dsar_messages.cases.actions.open_asset_cases)}
                </ColoredButton>
                {!editMode && (
                  <ColoredButton
                    className={styles.heroActionButton}
                    customColor='secondary'
                    disabled={false}
                    icon={false}
                    onClick={handleOpenEdit}
                    showLoader={false}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(dsar_messages.cases.actions.edit)}
                  </ColoredButton>
                )}
                {editMode && (
                  <ColoredButton
                    className={styles.heroActionButton}
                    customColor='secondary'
                    disabled={caseMutation.isLoading}
                    icon={false}
                    onClick={handleCancelEdit}
                    showLoader={false}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(general_messages.cancel)}
                  </ColoredButton>
                )}
                {!dsarCase.distributedAt && (
                  <ColoredButton
                    className={styles.heroActionButton}
                    customColor='warning'
                    disabled={deleteMutation.isLoading}
                    icon={false}
                    onClick={handleDelete}
                    showLoader={deleteMutation.isLoading}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(dsar_messages.cases.actions.delete)}
                  </ColoredButton>
                )}
                <ColoredButton
                  className={styles.heroActionButton}
                  customColor='secondary'
                  disabled={!canDistribute || distributeMutation.isLoading}
                  icon={false}
                  onClick={handleDistribute}
                  showLoader={distributeMutation.isLoading}
                  textLike={false}
                  variant='contained'
                >
                  {t(dsar_messages.cases.actions.send_to_asset_owners)}
                </ColoredButton>
              </div>
            </div>
            {!canDistribute && (
              <Typography className={clsx(styles.banner, styles.bannerWarning)} color='textPrimary'>
                {t(dsar_messages.cases.detail.identity_verification_required)}
              </Typography>
            )}
            {dsarCase.distributedAt && (
              <Typography className={clsx(styles.banner, styles.bannerInfo)} color='textPrimary'>
                {t(dsar_messages.cases.detail.distributed_banner)}
              </Typography>
            )}
          </Paper>

          <Paper className={styles.tabCard} elevation={0}>
            <Tabs
              aria-label={t(dsar_messages.cases.detail.title)}
              className={styles.tabs}
              indicatorColor='primary'
              onChange={(_, newValue) => setActiveTab(newValue)}
              textColor='primary'
              value={activeTab}
              variant='scrollable'
            >
              <Tab id='dsar-case-tab-0' label={t(dsar_messages.cases.detail.tab_case_details)} />
              <Tab id='dsar-case-tab-1' label={t(dsar_messages.cases.detail.tab_asset_handling)} />
              <Tab id='dsar-case-tab-2' label={t(dsar_messages.cases.detail.tab_close_case)} />
            </Tabs>

            <div className={styles.tabPanel}>
              <TabPanel index={0} value={activeTab}>
                <Paper className={styles.sectionCard} elevation={0}>
                  <Typography className={styles.sectionTitle} variant='h6'>
                    {t(dsar_messages.cases.detail.section_case)}
                  </Typography>
                  {editMode ? (
                    <div className={styles.formGrid}>
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterName'
                        label={t(dsar_messages.cases.create_dialog.requester_name)}
                        required
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterEmail'
                        label={t(dsar_messages.cases.create_dialog.requester_email)}
                        type='email'
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterPhone'
                        label={t(dsar_messages.cases.create_dialog.requester_phone)}
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterCustomerNumber'
                        label={t(dsar_messages.cases.create_dialog.requester_customernumber)}
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterSsn'
                        label={t(dsar_messages.cases.create_dialog.requester_ssn)}
                      />
                      <FormTextInput
                        className={styles.formFullWidth}
                        formik={formik}
                        fullWidth
                        id='requesterAddressLine1'
                        label={t(dsar_messages.cases.create_dialog.requester_address_line1)}
                      />
                      <FormTextInput
                        className={styles.formFullWidth}
                        formik={formik}
                        fullWidth
                        id='requesterAddressLine2'
                        label={t(dsar_messages.cases.create_dialog.requester_address_line2)}
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterPostalCode'
                        label={t(dsar_messages.cases.create_dialog.requester_postalcode)}
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterCity'
                        label={t(dsar_messages.cases.create_dialog.requester_city)}
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='requesterCountry'
                        label={t(dsar_messages.cases.create_dialog.requester_country)}
                      />
                      <FormSelect
                        formik={formik}
                        fullWidth
                        id='statusId'
                        label={t(dsar_messages.cases.create_dialog.status)}
                        options={toOptions(caseStatusValues)}
                        required
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='receivedAt'
                        inputLabelProps={{ shrink: true }}
                        label={t(dsar_messages.cases.create_dialog.received_at)}
                        required
                        type='date'
                      />
                      <FormTextInput
                        formik={formik}
                        fullWidth
                        id='dueDate'
                        inputLabelProps={{ shrink: true }}
                        label={t(dsar_messages.cases.create_dialog.due_date)}
                        required
                        type='date'
                      />
                    </div>
                  ) : (
                    <div className={styles.detailGrid}>
                      <Field label={t(dsar_messages.cases.create_dialog.requester_name)} value={dsarCase.requesterName} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_email)} value={dsarCase.requesterEmail} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_phone)} value={dsarCase.requesterPhone} />
                      <Field
                        label={t(dsar_messages.cases.create_dialog.requester_customernumber)}
                        value={dsarCase.requesterCustomerNumber}
                      />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_ssn)} value={dsarCase.requesterSsn} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_postalcode)} value={dsarCase.requesterPostalCode} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_city)} value={dsarCase.requesterCity} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_country)} value={dsarCase.requesterCountry} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_address_line1)} value={dsarCase.requesterAddressLine1} />
                      <Field label={t(dsar_messages.cases.create_dialog.requester_address_line2)} value={dsarCase.requesterAddressLine2} />
                      <Field label={t(dsar_messages.cases.create_dialog.status)} value={dsarCase.status.name} />
                      <Field label={t(dsar_messages.cases.create_dialog.received_at)} value={formatDate(dsarCase.receivedAt)} />
                      <Field label={t(dsar_messages.cases.create_dialog.due_date)} value={formatDate(dsarCase.dueDate)} />
                      <Field label={t(dsar_messages.cases.detail.closed_at)} value={formatDate(dsarCase.closedAt)} />
                    </div>
                  )}
                </Paper>

                <Paper className={styles.sectionCard} elevation={0}>
                  <Typography className={styles.sectionTitle} variant='h6'>
                    {t(dsar_messages.cases.detail.section_identity_verification)}
                  </Typography>
                  {editMode ? (
                    <div className={styles.groupedFields}>
                      <FormSelect
                        disabled={identityLocked}
                        formik={formik}
                        fullWidth
                        id='identityVerificationStatusId'
                        label={t(dsar_messages.cases.create_dialog.identity_verification_status)}
                        options={toOptions(identityStatusValues)}
                        required
                      />
                      <FormTextInput
                        disabled={identityLocked}
                        formik={formik}
                        fullWidth
                        id='identityVerificationNotes'
                        inputProps={{ className: styles.resizableTextarea }}
                        label={t(dsar_messages.cases.create_dialog.identity_verification_notes)}
                        multiline
                      />
                    </div>
                  ) : (
                    <div className={styles.detailGrid}>
                      <Field
                        label={t(dsar_messages.cases.create_dialog.identity_verification_status)}
                        value={dsarCase.identityVerificationStatus.name}
                      />
                      <Field
                        label={t(dsar_messages.cases.create_dialog.identity_verification_notes)}
                        value={dsarCase.identityVerificationNotes}
                      />
                    </div>
                  )}
                </Paper>

                <Paper className={styles.sectionCard} elevation={0}>
                  <Typography className={styles.sectionTitle} variant='h6'>
                    {t(dsar_messages.cases.detail.section_asset_owner_instructions)}
                  </Typography>
                  {editMode ? (
                    <div className={styles.formGrid}>
                      <div className={styles.formFullWidth}>
                        <FormControl
                          className={styles.formFullWidth}
                          error={formik.touched.requestTypeIds && Boolean(formik.errors.requestTypeIds)}
                          fullWidth
                        >
                          <InputLabel className={styles.checkboxGroupLabel} shrink>
                            {t(dsar_messages.cases.create_dialog.request_types)} *
                          </InputLabel>
                          <FormGroup className={styles.checkboxGroup}>
                            {requestTypeValues.map(requestType => (
                              <FormControlLabel
                                key={requestType.id}
                                control={
                                  <Checkbox
                                    checked={(formik.values.requestTypeIds || []).includes(requestType.id)}
                                    color='primary'
                                    disabled={requestTypesLocked}
                                    name={String(requestType.id)}
                                    onChange={handleRequestTypeChange}
                                  />
                                }
                                label={requestType.name}
                              />
                            ))}
                          </FormGroup>
                          <FormHelperText className={styles.checkboxError}>
                            {formik.touched.requestTypeIds ? (formik.errors.requestTypeIds as string) : ''}
                          </FormHelperText>
                        </FormControl>
                      </div>
                      <FormTextInput
                        className={styles.formFullWidth}
                        formik={formik}
                        fullWidth
                        id='adminInstructionComment'
                        inputProps={{ className: styles.resizableTextarea }}
                        label={t(dsar_messages.cases.create_dialog.admin_instruction_comment)}
                        multiline
                      />
                      <FormTextInput
                        className={styles.formFullWidth}
                        formik={formik}
                        fullWidth
                        id='instructionLink'
                        label={t(dsar_messages.cases.create_dialog.instruction_link)}
                      />
                      {showPortabilityFields && (
                        <>
                          <FormTextInput
                            className={styles.formFullWidth}
                            formik={formik}
                            fullWidth
                            id='portabilityInstruction'
                            inputProps={{ className: styles.resizableTextarea }}
                            label={t(dsar_messages.cases.create_dialog.portability_instruction)}
                            multiline
                          />
                          <FormTextInput
                            className={styles.formFullWidth}
                            formik={formik}
                            fullWidth
                            id='portabilityOutputLink'
                            label={t(dsar_messages.cases.create_dialog.portability_output_link)}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className={styles.requestTypes}>
                        {dsarCase.requestTypes.map(({ id: requestTypeId, requestType }) => (
                          <Chip key={requestTypeId} className={styles.requestTypeChip} label={requestType.name} size='small' />
                        ))}
                      </div>
                      <div className={styles.formFullWidth}>
                        <Field
                          label={t(dsar_messages.cases.create_dialog.admin_instruction_comment)}
                          value={dsarCase.adminInstructionComment}
                        />
                      </div>
                      <div className={styles.formFullWidth}>
                        <Field label={t(dsar_messages.cases.create_dialog.instruction_link)} value={dsarCase.instructionLink} />
                      </div>
                      {showReadOnlyPortabilityFields && (
                        <>
                          <div className={styles.formFullWidth}>
                            <Field
                              label={t(dsar_messages.cases.create_dialog.portability_instruction)}
                              value={dsarCase.portabilityInstruction}
                            />
                          </div>
                          <div className={styles.formFullWidth}>
                            <Field
                              label={t(dsar_messages.cases.create_dialog.portability_output_link)}
                              value={dsarCase.portabilityOutputLink}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {editMode && (
                    <div className={styles.sectionActions}>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={caseMutation.isLoading || domainValuesLoading}
                        icon={false}
                        onClick={saveCaseChanges}
                        showLoader={caseMutation.isLoading}
                        textLike={false}
                        variant='contained'
                      >
                        {t(general_messages.save)}
                      </ColoredButton>
                    </div>
                  )}
                </Paper>
              </TabPanel>

              <TabPanel index={1} value={activeTab}>
                <Paper className={styles.sectionCard} elevation={0}>
                  <Typography className={styles.sectionTitle} variant='h6'>
                    {t(dsar_messages.cases.detail.section_asset_handling)}
                  </Typography>
                  <Typography className={styles.helperText} variant='body2'>
                    {t(dsar_messages.cases.detail.section_asset_handling_intro)}
                  </Typography>
                  {caseAssetsLoading ? (
                    <Loader inner />
                  ) : (
                    caseAssets.map(caseAsset => (
                      <div key={caseAsset.id} className={styles.assetPanel}>
                        <CustomAccordion
                          contentClassName={styles.accordionContent}
                          heading={
                            <div className={styles.assetPanelHeader}>
                              <div>
                                <Typography variant='h6'>{caseAsset.asset.name}</Typography>
                                <div className={styles.assetPanelMeta}>
                                  <span>{`${t(dsar_messages.case_assets.table.owner)}: ${caseAsset.asset.owner.fullName}`}</span>
                                  <span>{`${t(dsar_messages.case_assets.table.status)}: ${caseAsset.status.name}`}</span>
                                  <span>{`${t(dsar_messages.case_assets.table.data_found)}: ${caseAsset.dataFound.name}`}</span>
                                  <span>{`${t(dsar_messages.case_assets.table.due_date)}: ${formatDate(caseAsset.dueDate)}`}</span>
                                </div>
                              </div>
                              <div className={styles.assetPanelBadges}>
                                {caseAsset.manualReviewRequired && (
                                  <Chip label={t(dsar_messages.case_assets.detail.manual_review_required)} size='small' />
                                )}
                                {caseAsset.thirdPartyDataRisk && (
                                  <Chip color='secondary' label={t(dsar_messages.case_assets.detail.third_party_data_risk)} size='small' />
                                )}
                              </div>
                            </div>
                          }
                          iconsSet={undefined}
                          id={`dsar-case-asset-panel-${caseAsset.id}`}
                        >
                          <div className={styles.detailGrid}>
                            <Field label={t(dsar_messages.case_assets.detail.result_summary)} value={caseAsset.resultSummary} />
                            <Field label={t(dsar_messages.case_assets.detail.asset_owner_comment)} value={caseAsset.assetOwnerComment} />
                          </div>
                          <table className={styles.requestResultTable}>
                            <thead>
                              <tr>
                                <th>{t(dsar_messages.cases.create_dialog.request_types)}</th>
                                <th>{t(dsar_messages.case_assets.table.status)}</th>
                                <th>{t(dsar_messages.case_assets.detail.result_summary)}</th>
                                <th>{t(dsar_messages.case_assets.detail.blocking_reason)}</th>
                                <th>{t(dsar_messages.case_assets.detail.earliest_possible_action_date)}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {caseAsset.requestTypeResults.map(result => (
                                <tr key={result.id}>
                                  <td>{result.requestType.name}</td>
                                  <td>{result.handlingStatus.name}</td>
                                  <td>{result.resultSummary || '-'}</td>
                                  <td>{result.blockingReason || '-'}</td>
                                  <td>{formatDate(result.earliestPossibleActionDate)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </CustomAccordion>
                      </div>
                    ))
                  )}
                </Paper>
              </TabPanel>

              <TabPanel index={2} value={activeTab}>
                <Paper className={styles.sectionCard} elevation={0}>
                  <Typography className={styles.sectionTitle} variant='h6'>
                    {t(dsar_messages.cases.detail.section_close_summary)}
                  </Typography>
                  <div className={styles.summaryGrid}>
                    {requestTypeSummaries.map(summary => {
                      const expanded = expandedSummaryCodes.includes(summary.code);
                      const pendingPreview = summary.pendingDetails.slice(0, PENDING_DETAILS_LIMIT);
                      const hiddenPendingCount = Math.max(summary.pendingDetails.length - PENDING_DETAILS_LIMIT, 0);
                      const detailCount = summary.blockingDetails.length + summary.pendingDetails.length;

                      return (
                        <div key={summary.code} className={styles.summaryPanel}>
                          <div className={styles.summaryHeader}>
                            <Typography gutterBottom variant='h6'>
                              {summary.name}
                            </Typography>
                            {detailCount > 0 && (
                              <button
                                aria-expanded={expanded}
                                className={styles.summaryDetailsButton}
                                onClick={() => toggleSummaryDetails(summary.code)}
                                type='button'
                              >
                                {expanded
                                  ? t(dsar_messages.cases.detail.hide_summary_details)
                                  : t(dsar_messages.cases.detail.show_summary_details, { count: detailCount })}
                              </button>
                            )}
                          </div>
                          <Typography variant='body2'>
                            <strong>{t(dsar_messages.cases.detail.request_type_summary_completed_assets)}:</strong>{' '}
                            {summary.completedAssets}
                          </Typography>
                          <Typography variant='body2'>
                            <strong>{t(dsar_messages.cases.detail.request_type_summary_not_completed_assets)}:</strong>{' '}
                            {summary.notCompletedAssets}
                          </Typography>
                          <Typography variant='body2'>
                            <strong>{t(dsar_messages.cases.detail.request_type_summary_blocking_assets)}:</strong>{' '}
                            {summary.blockingAssetNames.length
                              ? summary.blockingAssetNames.join(', ')
                              : t(dsar_messages.cases.detail.no_blocking_assets)}
                          </Typography>
                          <Typography variant='body2'>
                            <strong>{t(dsar_messages.cases.detail.request_type_summary_latest_planned_date)}:</strong>{' '}
                            {formatDate(summary.latestPlannedDate)}
                          </Typography>

                          <Collapse in={expanded && detailCount > 0} timeout='auto' unmountOnExit>
                            <div className={styles.summaryDetails}>
                              {summary.blockingDetails.length > 0 && (
                                <div>
                                  <Typography className={styles.summaryDetailHeading} variant='subtitle2'>
                                    {t(dsar_messages.cases.detail.summary_blocking_details)}
                                  </Typography>
                                  <table className={styles.summaryDetailTable}>
                                    <thead>
                                      <tr>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_asset)}</th>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_owner)}</th>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_reason)}</th>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_planned_date)}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {summary.blockingDetails.map(detail => (
                                        <tr key={`${summary.code}-blocking-${detail.assetId}`}>
                                          <td>{detail.assetName}</td>
                                          <td>{detail.ownerName}</td>
                                          <td>{detail.blockingReason || '-'}</td>
                                          <td>{formatDate(detail.plannedDate)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {summary.pendingDetails.length > 0 && (
                                <div>
                                  <Typography className={styles.summaryDetailHeading} variant='subtitle2'>
                                    {t(dsar_messages.cases.detail.summary_pending_details)}
                                  </Typography>
                                  <table className={styles.summaryDetailTable}>
                                    <thead>
                                      <tr>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_asset)}</th>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_owner)}</th>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_status)}</th>
                                        <th>{t(dsar_messages.cases.detail.summary_detail_planned_date)}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {pendingPreview.map(detail => (
                                        <tr key={`${summary.code}-pending-${detail.assetId}`}>
                                          <td>{detail.assetName}</td>
                                          <td>{detail.ownerName}</td>
                                          <td>{detail.statusName}</td>
                                          <td>{formatDate(detail.plannedDate)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {hiddenPendingCount > 0 && (
                                    <Typography className={styles.summaryDetailsHint} variant='body2'>
                                      {t(dsar_messages.cases.detail.summary_pending_hidden, { count: hiddenPendingCount })}
                                    </Typography>
                                  )}
                                </div>
                              )}
                            </div>
                          </Collapse>
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.formFullWidth}>
                    {editMode ? (
                      <FormTextInput
                        className={styles.formFullWidth}
                        formik={formik}
                        fullWidth
                        id='responseSummary'
                        inputProps={{ className: styles.resizableTextarea }}
                        label={t(dsar_messages.cases.detail.response_summary)}
                        multiline
                      />
                    ) : (
                      <Field label={t(dsar_messages.cases.detail.response_summary)} value={dsarCase.responseSummary} />
                    )}
                  </div>
                  {editMode && (
                    <div className={styles.sectionActions}>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={caseMutation.isLoading || domainValuesLoading}
                        icon={false}
                        onClick={saveCaseChanges}
                        showLoader={caseMutation.isLoading}
                        textLike={false}
                        variant='contained'
                      >
                        {t(general_messages.save)}
                      </ColoredButton>
                    </div>
                  )}
                </Paper>

                <Paper aria-labelledby='dsar-case-response-log-title' className={styles.sectionCard} component='section' elevation={0}>
                  <Typography className={styles.sectionTitle} id='dsar-case-response-log-title' variant='h6'>
                    {t(dsar_messages.cases.detail.section_response_log)}
                  </Typography>
                  <Typography className={styles.helperText} variant='body2'>
                    {t(dsar_messages.cases.detail.response_log_helper)}
                  </Typography>
                  {dsarCase.status.code !== 'closed' && (
                    <div className={clsx(styles.formGrid, styles.responseLogForm)}>
                      <FormSelect
                        className={styles.compactSelect}
                        formik={communicationLogFormik}
                        fullWidth
                        id='communicationType'
                        label={t(dsar_messages.cases.detail.communication_type)}
                        options={communicationTypeOptions}
                        required
                      />
                      <FormTextInput
                        className={styles.formFullWidth}
                        formik={communicationLogFormik}
                        fullWidth
                        id='comment'
                        inputProps={{ className: styles.logTextarea }}
                        label={t(dsar_messages.cases.detail.communication_comment)}
                        multiline
                        required
                        rows={3}
                      />
                      <div className={clsx(styles.formFullWidth, styles.responseLogActions)}>
                        <ColoredButton
                          className=''
                          customColor='secondary'
                          disabled={communicationLogMutation.isLoading}
                          icon={false}
                          onClick={handleAddCommunicationLog}
                          showLoader={communicationLogMutation.isLoading}
                          textLike={false}
                          variant='outlined'
                        >
                          {t(dsar_messages.cases.detail.add_response_log)}
                        </ColoredButton>
                      </div>
                    </div>
                  )}
                  <div aria-live='polite' className={styles.logList} role='list'>
                    {dsarCase.communicationLogs?.length ? (
                      dsarCase.communicationLogs.map(log => (
                        <div
                          key={log.id}
                          aria-label={`${
                            communicationTypeOptions.find(option => option.key === log.communicationType)?.label || log.communicationType
                          } log entry`}
                          className={styles.summaryPanel}
                          role='listitem'
                        >
                          <Typography gutterBottom variant='subtitle1'>
                            {communicationTypeOptions.find(option => option.key === log.communicationType)?.label || log.communicationType}
                          </Typography>
                          <div className={styles.detailGrid}>
                            <Field label={t(dsar_messages.cases.detail.sent_at)} value={formatDate(log.sentAt)} />
                            <Field label={t(dsar_messages.cases.detail.sent_by_user)} value={log.sentByUserName} />
                            <Field label={t(dsar_messages.cases.detail.days_since_received)} value={String(log.daysSinceReceived)} />
                            <Field label={t(dsar_messages.cases.detail.communication_comment)} value={log.comment} />
                          </div>
                        </div>
                      ))
                    ) : (
                      <Typography className={styles.helperText} variant='body2'>
                        {t(dsar_messages.cases.detail.no_response_logs)}
                      </Typography>
                    )}
                  </div>
                  <div className={styles.retentionBox}>
                    <Typography className={styles.sectionSubtitle} variant='subtitle1'>
                      {t(dsar_messages.cases.detail.section_retention)}
                    </Typography>
                    <Typography className={styles.retentionNotice} variant='body1'>
                      {retentionNotice}
                    </Typography>
                    <div className={styles.detailGrid}>
                      <Field label={t(dsar_messages.cases.detail.closed_at)} value={formatDate(dsarCase.closedAt)} />
                      <Field label={t(dsar_messages.cases.detail.closed_by_user)} value={dsarCase.closedByUserName} />
                      <Field label={t(dsar_messages.cases.detail.retention_until)} value={formatDate(dsarCase.retentionUntil)} />
                      <Field
                        label={t(dsar_messages.cases.detail.identifiers_anonymized_at)}
                        value={
                          dsarCase.identifiersAnonymizedAt
                            ? formatDate(dsarCase.identifiersAnonymizedAt)
                            : t(dsar_messages.cases.detail.not_anonymized_yet)
                        }
                      />
                    </div>
                    <Typography className={styles.helperText} variant='body2'>
                      {t(dsar_messages.cases.detail.retention_helper)}
                    </Typography>
                  </div>
                  <Typography className={styles.helperText} variant='body2'>
                    {t(dsar_messages.cases.detail.close_case_ready_helper)}
                  </Typography>
                  {dsarCase.status.code !== 'closed' && (
                    <div className={styles.sectionActions}>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={!canCloseCase || caseMutation.isLoading}
                        icon={false}
                        onClick={handleCloseCase}
                        showLoader={caseMutation.isLoading}
                        textLike={false}
                        variant='contained'
                      >
                        {t(dsar_messages.cases.detail.mark_as_completed)}
                      </ColoredButton>
                    </div>
                  )}
                </Paper>
              </TabPanel>
            </div>
          </Paper>
        </div>
        <Dialog fullWidth maxWidth='lg' onClose={handleCloseAssetSelectionDialog} open={assetSelectionDialogOpen}>
          <DialogTitle>{t(dsar_messages.cases.detail.send_assets_dialog_title)}</DialogTitle>
          <DialogContent dividers>
            <Typography className={styles.sectionSubtitle} variant='body2'>
              {t(dsar_messages.cases.detail.send_assets_dialog_intro)}
            </Typography>
            <div className={styles.assetSelectionToolbar}>
              <div className={styles.assetSelectionActions}>
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={eligibleAssetsLoading || !eligibleAssets.length}
                  icon={false}
                  onClick={handleSelectAllEligibleAssets}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(dsar_messages.cases.detail.send_assets_select_all)}
                </ColoredButton>
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={eligibleAssetsLoading || !eligibleAssets.length || !selectedAssetIds.length}
                  icon={false}
                  onClick={handleClearEligibleAssets}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(dsar_messages.cases.detail.send_assets_clear_all)}
                </ColoredButton>
              </div>
              <Typography color={selectedAssetIds.length ? 'textSecondary' : 'error'} variant='body2'>
                {selectedAssetsMessage}
              </Typography>
            </div>
            {assetSelectionContent}
          </DialogContent>
          <DialogActions>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={distributeMutation.isLoading}
              icon={false}
              onClick={handleCloseAssetSelectionDialog}
              showLoader={false}
              textLike={false}
              variant='outlined'
            >
              {t(general_messages.cancel)}
            </ColoredButton>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={!selectedAssetIds.length || eligibleAssetsLoading || distributeMutation.isLoading}
              icon={false}
              onClick={handleSendSelectedAssets}
              showLoader={distributeMutation.isLoading}
              textLike={false}
              variant='contained'
            >
              {t(dsar_messages.cases.detail.send_assets_submit)}
            </ColoredButton>
          </DialogActions>
        </Dialog>
      </Container>
    </ErrorBoundary>
  );
};

export default DsarCaseDetailPage;
